import * as _ from "lodash";

import { FormSchema } from "../../form/classes/schema";
import {
  FormSchemaData,
  FormSchemaEventFunction
} from "../../form/form.interface";

import { TableButton } from "./button";
import { TableColumn } from "./column";
import { TableRow } from "./row";

import {
  TableColumnLink,
  TableColumnInputSchemaEvent,
  TableColumnInputData,
  TableColumnFormatter
} from "../table.interface";

export class TableItem {
  constructor(
    public column: TableColumn,
    public row_data: any,
    public row: TableRow
  ) {
    this._setInput();
    this._setButtons();
    this.update();
  }

  private _label: string;
  private _title: string;
  private _buttons: Array<TableButton>;
  private _input: FormSchemaData;
  private _schema: FormSchema;

  private _has_link = false;
  private _has_link_with_action = false;
  private _has_link_with_url = false;
  private _has_buttons = false;
  private _has_input = false;

  public link: TableColumnLink;

  public get label(): string {
    return this._label || "";
  }

  public get title(): string {
    return this._title || "";
  }

  public get accessor(): string {
    return this.column.accessor;
  }

  public get input(): FormSchemaData {
    return this._input;
  }

  public get schema(): FormSchema {
    return this._schema;
  }

  public get buttons(): Array<TableButton> {
    return this._buttons
      ? this._buttons.filter(button => button.visible)
      : null;
  }

  public get numeric(): boolean {
    return this.column.numeric;
  }

  public get html(): boolean {
    return this.column.html;
  }

  public get width(): string {
    return this.column.width;
  }

  public get selectable(): boolean {
    return _.isBoolean(this.column.selectable) ? this.column.selectable : false;
  }

  public get truncate(): boolean {
    return _.isBoolean(this.column.truncate) ? this.column.truncate : false;
  }

  public get allow_select(): boolean {
    return this.column.selectable && this.row.selectable;
  }

  public get selected(): boolean {
    return this.row.selected;
  }

  public get has_link(): boolean {
    return this._has_link;
  }
  public get has_link_with_action(): boolean {
    return this._has_link_with_action;
  }
  public get has_link_with_url(): boolean {
    return this._has_link_with_url;
  }

  public get has_buttons(): boolean {
    return this._has_buttons;
  }

  public get has_input(): boolean {
    return this._has_input;
  }

  public update() {
    this._setAttributes();
    this._setLabel();
    this._setTitle();
    this._setLink();
    this._updateButtons();
  }

  public $setSchema(schema: FormSchema) {
    this._schema = schema;
  }

  private _setAttributes() {
    const link = this.column.link;
    this._has_link =
      link && (link.url || _.isFunction(link.action)) ? true : false;
    this._has_link_with_action = link && _.isFunction(link.action);
    this._has_link_with_url = link && !_.isFunction(link.action);
    this._has_buttons = this.buttons && this.buttons.length > 0;
    this._has_input = this._input ? true : false;
  }

  private _setButtons() {
    let buttons: Array<TableButton> = [];
    if (this.column.buttons) {
      buttons = this.column.buttons.map(button => {
        return new TableButton(button, this, this.row);
      });
    }
    this._buttons = buttons;
  }

  private _updateButtons() {
    if (this._buttons) {
      this._buttons.forEach(button => button.update());
    }
  }

  private _setInput() {
    let input: TableColumnInputData;
    if (this.column.input) {
      input = _.cloneDeep(this.column.input);
      if (input) {
        this._setLabel();
        input.key = this.column.accessor;
        if (this.row_data.id) {
          input.key += `-${this.row_data.id}`;
        } else {
          input.key += `-${this.row.index}`;
        }
        const events = this.column.input.events || {};
        if (!_.isEmpty(events)) {
          _.forEach(_.clone(events), (fn, key) => {
            if (
              _.includes(["onInit", "onChange", "onKeyup"], key) ||
              !_.isFunction(fn)
            ) {
              return;
            }
            events[key] = event => {
              event.table_row = this.row;
              event.table_item = this;
              event.data = this.row_data;
              fn(event);
            };
          });
        }
        input.events = _.merge({}, events, {
          onInit: event => this._onInputInit(event, events.onInit),
          onChange: event => this._onInputChange(event, events.onChange)
        });
        input.value = this._label;
      }
    }
    this._input = input;
  }

  private _setLabel() {
    if (_.isFunction(this.column.format)) {
      this._label = this.column.format(this.row_data);
    } else {
      const label = _.at(this.row_data, this.column.accessor)[0];
      this._label = label ? label.toString() : null;
    }
  }

  private _setTitle() {
    this._title = _.isFunction(this.column.title)
      ? (<TableColumnFormatter>this.column.title)(this.row_data)
      : this.html
      ? this.label.replace(/<(?:.|\n)*?>/gm, "")
      : this.label;
  }

  private _setLink() {
    if (!this._has_link) {
      this.link = null;
      return;
    } else {
      const link = this.column.link;
      if (link.action) {
        const action = (event: MouseEvent) => {
          event.preventDefault();
          link.action(this.row_data);
        };
        this.link = {
          action: action
        };
      } else {
        this.link = {
          url: _.isFunction(link.url)
            ? (<any>link.url)(this.row_data)
            : link.url,
          target: link.target
        };
      }
    }
  }

  private _onInputInit(
    event: TableColumnInputSchemaEvent,
    callback_event: FormSchemaEventFunction<TableColumnInputSchemaEvent>
  ) {
    if (_.isFunction(callback_event)) {
      event.table_row = this.row;
      event.table_item = this;
      event.data = this.row_data;
      if (_.isFunction(callback_event)) {
        callback_event(event);
      }
    }
  }

  private _onInputChange(
    event: TableColumnInputSchemaEvent,
    callback_event: FormSchemaEventFunction<TableColumnInputSchemaEvent>
  ) {
    this._input.value = event.schema.value;
    _.set(this.row_data, this.column.accessor, event.schema.value);
    if (_.isFunction(callback_event)) {
      event.table_row = this.row;
      event.table_item = this;
      event.data = this.row_data;
      if (_.isFunction(callback_event)) {
        callback_event(event);
      }
    }
    this.row.update();
  }
}
